import React, { useState, useEffect } from "react";
import {
    Loader2, Send, CheckCircle, AlertCircle,
    MessageCircle, Search, ChevronDown, X, Hash, User
} from "lucide-react";
import {
    fetchWhatsAppVendors,
    fetchWhatsAppAppNames,
    fetchWhatsAppTemplates,
    sendWhatsAppNotification,
} from "./whatsappConfigService";
import { useWebSocket } from "../../websocket/WebSocketContext";
import ComingSoonModal from "../../pages/ComingSoonModal";

// ─── Extract {{N}} placeholders from template message ────────────────────────
const extractAttributes = (message) => {
    const regex = /\{\{(\d+)\}\}/g;
    const attrs = [];
    let match;
    const seen = new Set();
    while ((match = regex.exec(message)) !== null) {
        if (!seen.has(match[1])) {
            seen.add(match[1]);
            attrs.push({ attributeName: match[1], attributeValue: "" });
        }
    }
    return attrs;
};

// ─── Template Search Dropdown ─────────────────────────────────────────────────
function TemplateDropdown({ templates, selected, onSelect, loading }) {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState("");

    const filtered = templates.filter(t =>
        t.campaignId.toLowerCase().includes(query.toLowerCase()) ||
        t.templateId.toLowerCase().includes(query.toLowerCase()) ||
        t.message.toLowerCase().includes(query.toLowerCase())
    );

    return (
        <div className="relative">
            <button
                type="button"
                onClick={() => setOpen(o => !o)}
                className="w-full flex items-center justify-between px-4 py-3 bg-black/40 border border-white/20 rounded-xl text-white hover:border-emerald-500/50 transition-all"
            >
                <span className={selected ? "text-white text-sm" : "text-white/40 text-sm"}>
                    {selected
                        ? `[${selected.campaignId}] ${selected.message.slice(0, 60)}${selected.message.length > 60 ? "…" : ""}`
                        : loading ? "Loading templates…" : "Select a WhatsApp template"}
                </span>
                {loading
                    ? <Loader2 className="w-4 h-4 animate-spin text-white/40" />
                    : <ChevronDown className={`w-4 h-4 text-white/40 transition-transform ${open ? "rotate-180" : ""}`} />
                }
            </button>

            {open && (
                <div className="absolute z-50 mt-2 w-full bg-slate-900 border border-white/10 rounded-xl shadow-2xl overflow-hidden">
                    <div className="p-2 border-b border-white/10">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/40" />
                            <input
                                autoFocus
                                type="text"
                                placeholder="Search by campaign ID, template ID or message…"
                                value={query}
                                onChange={e => setQuery(e.target.value)}
                                className="w-full pl-9 pr-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm text-white placeholder-white/30 focus:outline-none focus:ring-1 focus:ring-emerald-500/50"
                            />
                            {query && (
                                <button onClick={() => setQuery("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/60">
                                    <X className="w-3.5 h-3.5" />
                                </button>
                            )}
                        </div>
                    </div>

                    <ul className="max-h-60 overflow-y-auto divide-y divide-white/5">
                        {filtered.length === 0 ? (
                            <li className="px-4 py-6 text-center text-sm text-white/40">No templates found</li>
                        ) : filtered.map(t => (
                            <li key={t._id}>
                                <button
                                    type="button"
                                    onClick={() => { onSelect(t); setOpen(false); setQuery(""); }}
                                    className={`w-full text-left px-4 py-3 hover:bg-white/5 transition-colors ${selected?._id === t._id ? "bg-emerald-500/10" : ""}`}
                                >
                                    <div className="flex items-center gap-2 mb-1">
                                        <span className="text-xs px-1.5 py-0.5 bg-emerald-500/20 text-emerald-300 rounded font-mono">
                                            C: {t.campaignId}
                                        </span>
                                        <span className="text-xs px-1.5 py-0.5 bg-teal-500/20 text-teal-300 rounded font-mono">
                                            T: {t.templateId}
                                        </span>
                                    </div>
                                    <p className="text-sm text-slate-200 font-mono leading-relaxed">{t.message}</p>
                                </button>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
}

// ─── Attribute Value Inputs ───────────────────────────────────────────────────
function AttributeInputs({ attributes, onChange }) {
    if (attributes.length === 0) return null;
    return (
        <div className="space-y-3">
            <label className="block text-white/80 text-sm font-medium">
                Template Variables
            </label>
            <div className="bg-white/5 border border-white/10 rounded-xl p-4 space-y-3">
                {attributes.map((attr, idx) => (
                    <div key={attr.attributeName} className="flex items-center gap-3">
                        <div className="flex items-center gap-1.5 min-w-[80px]">
                            <Hash className="w-3.5 h-3.5 text-emerald-400" />
                            <span className="text-sm text-emerald-300 font-mono font-semibold">
                                {`{{${attr.attributeName}}}`}
                            </span>
                        </div>
                        <input
                            type="text"
                            placeholder={`Value for {{${attr.attributeName}}}…`}
                            value={attr.attributeValue}
                            onChange={e => onChange(idx, e.target.value)}
                            className="flex-1 px-3 py-2 bg-black/40 border border-white/15 rounded-lg text-sm text-white placeholder-white/30 focus:outline-none focus:ring-1 focus:ring-emerald-500/50 transition-all"
                        />
                    </div>
                ))}
            </div>
        </div>
    );
}

// ─── Main WhatsApp Form ───────────────────────────────────────────────────────
export default function WhatsAppForm({ setIsLoading, setSuccess, setError, API_BASE_URL, loginId, deliveryMode }) {
    const { client } = useWebSocket();
    const ctx = { deliveryMode, client, loginId };

    // Dropdowns
    const [vendors, setVendors] = useState([]);
    const [appNames, setAppNames] = useState([]);
    const [templates, setTemplates] = useState([]);

    // Form fields
    const [vendor, setVendor] = useState("");
    const [appName, setAppName] = useState("");
    const [waType, setWaType] = useState("SINGLE");
    const [mobileInput, setMobileInput] = useState("");
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [attributes, setAttributes] = useState([]);

    // UI state
    const [loadingVendors, setLoadingVendors] = useState(false);
    const [loadingAppNames, setLoadingAppNames] = useState(false);
    const [loadingTemplates, setLoadingTemplates] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [successLocal, setSuccessLocal] = useState("");
    const [errorLocal, setErrorLocal] = useState("");

    // Coming Soon modal
    const [showComingSoon, setShowComingSoon] = useState(false);

    // ── Load vendors ──────────────────────────────────────────────────────────
    useEffect(() => {
        const load = async () => {
            setLoadingVendors(true);
            try {
                const list = await fetchWhatsAppVendors(ctx);
                setVendors(list);
                setVendor(prev => prev || list[0] || "");
            } catch (e) {
                console.error(e);
                setErrorLocal("Failed to load vendors.");
            } finally {
                setLoadingVendors(false);
            }
        };
        load();
    }, [deliveryMode]);

    // ── Load app names ────────────────────────────────────────────────────────
    useEffect(() => {
        const load = async () => {
            setLoadingAppNames(true);
            try {
                const list = await fetchWhatsAppAppNames(ctx);
                setAppNames(list);
                setAppName(prev => prev || list[0] || "");
            } catch (e) {
                console.error(e);
            } finally {
                setLoadingAppNames(false);
            }
        };
        load();
    }, [deliveryMode]);

    // ── Load templates ────────────────────────────────────────────────────────
    useEffect(() => {
        const load = async () => {
            setLoadingTemplates(true);
            try {
                const list = await fetchWhatsAppTemplates(ctx);
                setTemplates(list);
            } catch (e) {
                console.error(e);
                setErrorLocal("Failed to load templates.");
            } finally {
                setLoadingTemplates(false);
            }
        };
        load();
    }, [deliveryMode]);

    // ── Auto-clear messages ───────────────────────────────────────────────────
    useEffect(() => {
        if (!successLocal && !errorLocal) return;
        const t = setTimeout(() => { setSuccessLocal(""); setErrorLocal(""); }, 4000);
        return () => clearTimeout(t);
    }, [successLocal, errorLocal]);

    // ── Template selected → extract attributes ────────────────────────────────
    const handleTemplateSelect = (template) => {
        setSelectedTemplate(template);
        setAttributes(extractAttributes(template.message));
    };

    const handleAttributeChange = (idx, value) => {
        setAttributes(prev => prev.map((a, i) => i === idx ? { ...a, attributeValue: value } : a));
    };

    // ── Type toggle — intercept BULK ─────────────────────────────────────────
    const handleTypeChange = (type) => {
        if (type === "BULK") {
            setShowComingSoon(true);
            // Do NOT change waType — stay on SINGLE
            return;
        }
        setWaType(type);
        setMobileInput("");
    };

    // ── Validate mobile numbers ───────────────────────────────────────────────
    const validateMobiles = (input, bulk) => {
        const nums = input
            .split(/[\n,]+/)
            .map(n => n.trim())
            .filter(Boolean);

        if (nums.length === 0) return { ok: false, msg: "Please enter at least one mobile number." };

        for (const n of nums) {
            if (!/^[0-9]{10,15}$/.test(n)) {
                return { ok: false, msg: `Invalid mobile number "${n}". Must be 10–15 digits only.` };
            }
        }

        if (!bulk && nums.length > 1) {
            return { ok: false, msg: "Only one mobile number is allowed in SINGLE mode." };
        }

        return { ok: true, nums };
    };

    // ── Submit ────────────────────────────────────────────────────────────────
    const handleSend = async () => {
        setSuccessLocal("");
        setErrorLocal("");

        if (!vendor) return setErrorLocal("Please select a WhatsApp vendor.");
        if (!appName) return setErrorLocal("Please select an app name.");
        if (!selectedTemplate) return setErrorLocal("Please select a template.");

        const { ok, msg, nums } = validateMobiles(mobileInput, waType === "BULK");
        if (!ok) return setErrorLocal(msg);

        for (const a of attributes) {
            if (!a.attributeValue.trim()) {
                return setErrorLocal(`Please fill value for {{${a.attributeName}}}.`);
            }
        }

        setIsSubmitting(true);
        if (typeof setIsLoading === "function") setIsLoading(true);

        try {
            const basePayload = {
                Type: waType,
                campaignId: selectedTemplate.campaignId,
                appName,
                whatsapp_vendor_name: vendor,
                ...(attributes.length > 0 && { attributeDetails: attributes }),
            };

            if (waType === "BULK") {
                for (const mobileNo of nums) {
                    await sendWhatsAppNotification(ctx, { ...basePayload, mobileNo });
                }
            } else {
                await sendWhatsAppNotification(ctx, { ...basePayload, mobileNo: nums[0] });
            }

            setSuccessLocal("WhatsApp message queued successfully!");
            setMobileInput("");
            setSelectedTemplate(null);
            setAttributes([]);
        } catch (err) {
            console.error(err);
            setErrorLocal(err.message || "Failed to send. Please try again.");
        } finally {
            setIsSubmitting(false);
            if (typeof setIsLoading === "function") setIsLoading(false);
        }
    };

    // ── Live preview ──────────────────────────────────────────────────────────
    const getPreview = () => {
        if (!selectedTemplate) return null;
        let msg = selectedTemplate.message;
        attributes.forEach(a => {
            msg = msg.replace(new RegExp(`\\{\\{${a.attributeName}\\}\\}`, "g"), a.attributeValue || `{{${a.attributeName}}}`);
        });
        return msg;
    };

    const preview = getPreview();

    return (
        <>
            <div className="space-y-6">

                {/* Status banner */}
                {(successLocal || errorLocal) && (
                    <div className={`p-4 rounded-xl flex items-center space-x-3 border ${successLocal
                        ? "bg-green-500/10 border-green-500/30 text-green-200"
                        : "bg-red-500/10 border-red-500/30 text-red-200"}`}>
                        {successLocal
                            ? <CheckCircle className="w-5 h-5 flex-shrink-0" />
                            : <AlertCircle className="w-5 h-5 flex-shrink-0" />}
                        <span className="text-sm font-medium">{successLocal || errorLocal}</span>
                    </div>
                )}

                {/* Row 1: Vendor + Type */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-white/80 text-sm mb-2 font-medium">WhatsApp Vendor</label>
                        <select
                            value={vendor}
                            onChange={e => setVendor(e.target.value)}
                            disabled={loadingVendors}
                            className="w-full px-4 py-3 bg-black/40 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-emerald-500/50 transition-all disabled:opacity-50"
                        >
                            {loadingVendors
                                ? <option>Loading…</option>
                                : vendors.length > 0
                                    ? vendors.map(v => <option key={v} value={v}>{v}</option>)
                                    : <option value="">No vendors found</option>
                            }
                        </select>
                    </div>

                    <div>
                        <label className="block text-white/80 text-sm mb-2 font-medium">Send Type</label>
                        <div className="flex items-center bg-white/5 border border-white/20 rounded-xl p-1 h-[50px]">
                            {["SINGLE", "BULK"].map(type => (
                                <button
                                    key={type}
                                    type="button"
                                    onClick={() => handleTypeChange(type)}
                                    className={`
                                        relative flex-1 py-2 rounded-lg text-sm font-medium transition-all
                                        ${waType === type && type === "SINGLE"
                                            ? "bg-gradient-to-r from-emerald-500 to-teal-500 text-white shadow-md"
                                            : "text-white/50 hover:text-white"}
                                        ${type === "BULK" ? "group" : ""}
                                    `}
                                >
                                    {type}
                                    {/* Subtle "soon" badge on BULK */}
                                    {type === "BULK" && (
                                        <span className="absolute -top-1.5 -right-1 text-[9px] px-1 py-0.5 bg-amber-500/80 text-white rounded font-bold leading-none opacity-80 group-hover:opacity-100 transition-opacity">
                                            SOON
                                        </span>
                                    )}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                {/* App Name */}
                <div>
                    <label className="block text-white/80 text-sm mb-2 font-medium">App Name</label>
                    <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                        <input
                            list="wa-app-names"
                            value={appName}
                            onChange={e => setAppName(e.target.value)}
                            placeholder={loadingAppNames ? "Loading app names…" : "Type or select app name…"}
                            className="w-full pl-10 pr-4 py-3 bg-black/40 border border-white/20 rounded-xl text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 transition-all"
                        />
                        <datalist id="wa-app-names">
                            {appNames.map(n => <option key={n} value={n} />)}
                        </datalist>
                    </div>
                </div>

                {/* Mobile Number */}
                <div>
                    <label className="block text-white/80 text-sm mb-2 font-medium">
                        Mobile Number (10–15 digits)
                    </label>
                    <textarea
                        value={mobileInput}
                        onChange={e => setMobileInput(e.target.value)}
                        rows={1}
                        placeholder="9876543210"
                        className="w-full px-4 py-3 bg-black/40 border border-white/20 rounded-xl text-white placeholder-white/30 resize-none focus:outline-none focus:ring-2 focus:ring-emerald-500/50 transition-all font-mono text-sm"
                    />
                </div>

                {/* Template selector */}
                <div>
                    <label className="block text-white/80 text-sm mb-2 font-medium">WhatsApp Template</label>
                    <TemplateDropdown
                        templates={templates}
                        selected={selectedTemplate}
                        onSelect={handleTemplateSelect}
                        loading={loadingTemplates}
                    />
                </div>

                {/* Attribute inputs */}
                <AttributeInputs attributes={attributes} onChange={handleAttributeChange} />

                {/* Live preview */}
                {preview && (
                    <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-xl p-4">
                        <p className="text-xs text-emerald-400 font-semibold mb-2 uppercase tracking-wide">Message Preview</p>
                        <p className="text-sm text-slate-200 font-mono leading-relaxed whitespace-pre-wrap">{preview}</p>
                    </div>
                )}

                {/* Send button */}
                <button
                    onClick={handleSend}
                    disabled={isSubmitting}
                    className="w-full py-3.5 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white font-semibold shadow-lg shadow-emerald-500/20 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    {isSubmitting ? (
                        <div className="flex items-center justify-center space-x-2">
                            <Loader2 className="w-5 h-5 animate-spin" />
                            <span>Sending…</span>
                        </div>
                    ) : (
                        <div className="flex items-center justify-center space-x-2">
                            <MessageCircle className="w-5 h-5" />
                            <span>Send WhatsApp</span>
                        </div>
                    )}
                </button>
            </div>

            {/* ── Coming Soon Modal ── */}
            <ComingSoonModal
                isOpen={showComingSoon}
                onClose={() => setShowComingSoon(false)}
                featureName="Bulk WhatsApp"
                description="Send a single message to thousands of contacts at once. We're building it with smart queuing, rate limiting, and real-time delivery tracking."
                accentColor="emerald"
            />
        </>
    );
}